/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { Tooltip } from "@material-ui/core";
import { Button } from "components/haven-design/buttons";
import { apolloClient as client } from "data/graphql";
import logger from "services/logger";
import { Viewer } from "components/auth/types";
import { useSnackbar } from "components/haven-design/snackbar";
import { ElasticQuery, FormType, sendFormToInterests, sendFormToInterestsVariables, skipInterestsAction, skipInterestsActionVariables } from "types/gql-types";
import ReasonModal, { Action } from "pages/dashboard/interests-profile/components/reason-modal";
import { SKIP_INTERESTS, SEND_FULL_APP_TO_INTERESTS } from "pages/dashboard/interests-profile/components/actions-query";
import SendFullAppModal from "../send-full-app-modal";
import { TableResult, useInterestsTable } from "../interests-table/table-context";
import { formatFilters, useSidebarFilters } from "../sidebar-filters";
import { MessageButton } from "./button-message";

import CsvButton from "./button-csv";
import ButtonChangeStatus from "./button-change-status";
import SkipButton from "./button-action-skip";


const getScope = (
  withQuery: boolean,
  elasticQuery: ElasticQuery | null,
  interestIds: string[],
) => ({
  interestIds: withQuery ? null : interestIds,
  elasticQuery: (withQuery && elasticQuery) || null,
});


type ActionButtonType = {
  sendFullAppToInterestsEnabled: boolean;
  selectedNames: TableResult[];
  interestListId: string;
  viewer: Viewer;
  setMessageModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCSVModal: (value: boolean) => void;
  onStatusChange: (status: string, reason: string | null) => Promise<any>;
  statusChangeLoading: boolean;
};

const ActionButton: React.FC<ActionButtonType> = ({
  sendFullAppToInterestsEnabled,
  selectedNames,
  interestListId,
  viewer,
  setMessageModalOpen,
  onStatusChange,
  setShowCSVModal,
  statusChangeLoading,
}: ActionButtonType) => {
  const { openSnackbar } = useSnackbar();
  const {
    selectedInterestIds,
    hasSelectedAllInterests,
    selectedPageRange,
    totalSelectedInterests,
  } = useInterestsTable();
  const { search, interestListIds } = useSidebarFilters();

  const [sendFullAppToInterests] =
    useMutation<sendFormToInterests, sendFormToInterestsVariables>(
      SEND_FULL_APP_TO_INTERESTS,
      { client },
    );
  const [skipInterests, { loading: isSkippingInterests }] =
  useMutation<skipInterestsAction, skipInterestsActionVariables>(SKIP_INTERESTS);
  const [isSendFullAppOpen, setIsSendFullAppOpen] = React.useState(false);
  const [reason, setReason] = useState("");
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const closeReasonModal = () => setSkipModalOpen(false);
  let waitForIndex = false;

  const {
    filterState,
    textFilter,
  } = useSidebarFilters();

  const elasticQuery = {
    filters: formatFilters(filterState),
    textFilter,
  };

  const withQuery = hasSelectedAllInterests || (
    selectedPageRange.from !== null || selectedPageRange.to !== null
  );


  const onSendFullApp = (deadline: Date) => {
    const variables = {
      interestIds: selectedInterestIds,
      type: FormType.FULL_APPLICATION,
      deadline: deadline.toISOString().slice(0, 10),
    };

    sendFullAppToInterests({ variables })
      .then(() => {
        openSnackbar({ message: "Full application request sent.", status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "There was a problem sending full application request.", status: "error" });
      })
      .finally(() => {
        setIsSendFullAppOpen(false);
      });
  };

  const onSkipInterests = () => {
    const variables = {
      interestListIds: [interestListId],
      reason,
      pageFrom: selectedPageRange.from,
      pageTo: selectedPageRange.to,
      ...getScope(withQuery, elasticQuery, selectedInterestIds),
    };

    if (!reason) {
      openSnackbar({ message: "Reason can't be empty", status: "error", duration: 5000 });
      return;
    }
    skipInterests({ variables })
      .then(() => {
        waitForIndex = true;
        openSnackbar({ message: `${totalSelectedInterests} applicants skipped.`, status: "success", duration: 5000 });

        setTimeout(() => {
          search();
          waitForIndex = false;
        // wait for 2 extra seconds in case index hasn't propagated the changes to all indexes
        }, 2000);
      })
      .catch((err) => {
        const errMsg = `
          Error skipping applicants. Please refresh before retrying.
          If the problem persists please contact support@havenconnect.com`;
        openSnackbar({ message: errMsg, status: "error", duration: 5000 });
        logger.sendError(err);
      })
      .finally(closeReasonModal);
  };

  const hasSelectedInterests = totalSelectedInterests > 0;

  const button = <>
    <PopupState variant="popover" popupId="interests-toolbar-actions-menu">
      {(popupState) => (
        <>
          <Button id="interests-toolbar-actions" color="primary" {...bindTrigger(popupState)} disabled={!hasSelectedInterests} style={{ height: "100%" }}>
            Bulk Actions {totalSelectedInterests > 0 && `(${totalSelectedInterests})`}
            <span className="caret" style={{ marginLeft: 4 }} />
          </Button>
          <Menu {...bindMenu(popupState)}>
            {sendFullAppToInterestsEnabled && interestListIds.length === 1 && (
              <MenuItem
                disabled={selectedInterestIds.length === 0}
                id="send-full-application"
                onMouseUp={() => {
                  setIsSendFullAppOpen(true);
                  popupState.close();
                }}
              >Send Full Application {`(${selectedInterestIds.length})`}
              </MenuItem>
            )}
            {interestListIds.length === 1 && (
              <MessageButton
                setMessageModalOpen={setMessageModalOpen}
                viewer={viewer}
                recipients={selectedNames}
                currentListId={interestListId}
                actionsButton
                closeMenu={popupState.close}
              />)}
            <ButtonChangeStatus
              onStatusChange={onStatusChange}
              isAvailable={hasSelectedInterests}
              actionsButton
              closeMenu={popupState.close}
              loading={statusChangeLoading}
            />
            <CsvButton
              viewer={viewer}
              actionsButton
              closeMenu={popupState.close}
              setShowCSVModal={setShowCSVModal}
            />
            {interestListIds.length === 1 && (
              <SkipButton
                selectedInterestIds={selectedInterestIds.slice()}
                closeMenu={popupState.close}
                setSkipModalOpen={setSkipModalOpen}
              />)}
          </Menu>
        </>
      )}
    </PopupState>
    <SendFullAppModal
      interestCount={selectedInterestIds.length}
      onSubmit={(deadline: Date) => onSendFullApp(deadline)}
      onRequestClose={() => setIsSendFullAppOpen(false)}
      onCancel={() => setIsSendFullAppOpen(false)}
      isOpen={isSendFullAppOpen}
    />
    <ReasonModal
      isOpen={skipModalOpen}
      newStatus={Action.Skip}
      currentStatus={Action.On_Waitlist}
      onSubmit={() => onSkipInterests()}
      onChange={(input) => setReason(input)}
      reason={reason}
      onRequestClose={closeReasonModal}
      onCancel={closeReasonModal}
      isSubmitting={isSkippingInterests || waitForIndex}
      bulkAction
    />
  </>;
  if (totalSelectedInterests === 50000) {
    return <Tooltip title="Search is too large to perform bulk actions">
      <div style={{ height: "100%" }}>Disabled</div>
    </Tooltip>;
  }

  return hasSelectedInterests ? button : <Tooltip title="Select Applications First">
    <div style={{ height: "100%" }}>{button}</div>
  </Tooltip>;
};

export default ActionButton;
